import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dadosIniciais } from './manual-defaults';
import { avaliarManual } from './manual-progresso';
import {
  buscarManual,
  congelarVersao,
  restaurarVersao,
  salvarManual,
  type ManualResumo,
  type MotivoVersao,
  type StatusManual,
} from './manual-api';
import {
  carregarRascunho,
  chaveDoRascunho,
  desserializarManual,
  removerRascunho,
  salvarRascunho,
  serializarManual,
} from './manual-storage';
import { baixarArquivo, normalizarNomeArquivo } from './manual-utils';
import type { ItemBase, ManualData, ManualOpcoes } from './manual-types';

/** Campos de `ManualData` que guardam listas dinâmicas. */
export type CampoLista = {
  [K in keyof ManualData]: ManualData[K] extends ItemBase[] ? K : never;
}[keyof ManualData];

export type EstadoSalvamento = 'ocioso' | 'pendente' | 'salvo' | 'erro';

/**
 * Onde o documento está sendo gravado. `servidor` é o modo normal (manual do
 * histórico); `navegador` só acontece quando a tela é aberta sem manual — um
 * rascunho solto, que existe para ninguém perder trabalho antes de dar nome ao
 * documento.
 */
export type DestinoSalvamento = 'servidor' | 'navegador';

const LIMITE_HISTORICO = 40;
const ATRASO_SALVAMENTO = 1200;

export interface OpcoesEditor {
  projetoId?: string | number | null;
  manualId?: string | null;
}

export function useManualEditor({ projetoId, manualId }: OpcoesEditor) {
  const chave = useMemo(() => chaveDoRascunho(projetoId), [projetoId]);
  const destino: DestinoSalvamento = manualId ? 'servidor' : 'navegador';

  const [dados, setDados] = useState<ManualData>(dadosIniciais);
  const [manual, setManual] = useState<ManualResumo | null>(null);
  const [estado, setEstado] = useState<EstadoSalvamento>('ocioso');
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregado = useRef(false);
  const passado = useRef<ManualData[]>([]);
  const futuro = useRef<ManualData[]>([]);
  const paginas = useRef(0);
  const [marcaHistorico, setMarcaHistorico] = useState(0);
  /** Sobe a cada gravação de versão/restauração, para o painel recarregar. */
  const [marcaVersoes, setMarcaVersoes] = useState(0);

  const avaliacao = useMemo(() => avaliarManual(dados), [dados]);
  const avaliacaoRef = useRef(avaliacao);
  avaliacaoRef.current = avaliacao;

  const metricas = useCallback(() => ({
    progresso: avaliacaoRef.current.geral,
    pendenciasCriticas: avaliacaoRef.current.pendenciasCriticas,
    totalPaginas: paginas.current,
  }), []);

  /* -------- carga -------- */
  useEffect(() => {
    carregado.current = false;
    passado.current = [];
    futuro.current = [];
    setMarcaHistorico((valor) => valor + 1);
    setErro(null);

    if (!manualId) {
      const rascunho = carregarRascunho(chave);
      setManual(null);
      setDados(rascunho ?? dadosIniciais());
      setEstado(rascunho ? 'salvo' : 'ocioso');
      setSalvoEm(null);
      const id = window.setTimeout(() => { carregado.current = true; }, 0);
      return () => window.clearTimeout(id);
    }

    let ativo = true;
    setCarregando(true);
    buscarManual(manualId)
      .then(({ manual: registro, dados: documento }) => {
        if (!ativo) return;
        setManual(registro);
        setDados(documento);
        setEstado('salvo');
        setSalvoEm(new Date(registro.updatedAt));
        window.setTimeout(() => { carregado.current = true; }, 0);
      })
      .catch((falha: unknown) => {
        if (!ativo) return;
        setErro(falha instanceof Error ? falha.message : 'Não foi possível abrir o manual.');
        setEstado('erro');
      })
      .finally(() => { if (ativo) setCarregando(false); });

    return () => { ativo = false; };
  }, [chave, manualId]);

  /* -------- histórico de edição -------- */
  const registrar = useCallback((anterior: ManualData) => {
    passado.current = [...passado.current.slice(-LIMITE_HISTORICO), anterior];
    futuro.current = [];
    setMarcaHistorico((valor) => valor + 1);
  }, []);

  const aplicar = useCallback((transformar: (atual: ManualData) => ManualData) => {
    setDados((atual) => {
      const proximo = transformar(atual);
      if (proximo === atual) return atual;
      registrar(atual);
      return proximo;
    });
    setEstado('pendente');
  }, [registrar]);

  const desfazer = useCallback(() => {
    if (!passado.current.length) return;
    setDados((atual) => {
      const anterior = passado.current[passado.current.length - 1];
      passado.current = passado.current.slice(0, -1);
      futuro.current = [atual, ...futuro.current].slice(0, LIMITE_HISTORICO);
      setMarcaHistorico((valor) => valor + 1);
      return anterior;
    });
    setEstado('pendente');
  }, []);

  const refazer = useCallback(() => {
    if (!futuro.current.length) return;
    setDados((atual) => {
      const proximo = futuro.current[0];
      futuro.current = futuro.current.slice(1);
      passado.current = [...passado.current, atual].slice(-LIMITE_HISTORICO);
      setMarcaHistorico((valor) => valor + 1);
      return proximo;
    });
    setEstado('pendente');
  }, []);

  /* -------- edição -------- */
  const definirCampo = useCallback(<K extends keyof ManualData>(campo: K, valor: ManualData[K]) => {
    aplicar((atual) => (atual[campo] === valor ? atual : { ...atual, [campo]: valor }));
  }, [aplicar]);

  const definirOpcao = useCallback(<K extends keyof ManualOpcoes>(opcao: K, valor: ManualOpcoes[K]) => {
    aplicar((atual) => ({ ...atual, opcoes: { ...atual.opcoes, [opcao]: valor } }));
  }, [aplicar]);

  const adicionarItens = useCallback(<K extends CampoLista>(campo: K, itens: ManualData[K]) => {
    aplicar((atual) => ({ ...atual, [campo]: [...(atual[campo] as ItemBase[]), ...(itens as ItemBase[])] }));
  }, [aplicar]);

  const adicionarItem = useCallback(<K extends CampoLista>(campo: K, item: ManualData[K][number]) => {
    aplicar((atual) => ({ ...atual, [campo]: [...(atual[campo] as ItemBase[]), item as ItemBase] }));
  }, [aplicar]);

  const atualizarItem = useCallback(<K extends CampoLista>(campo: K, id: string, patch: Partial<ManualData[K][number]>) => {
    aplicar((atual) => ({
      ...atual,
      [campo]: (atual[campo] as ItemBase[]).map((item) => (item.id === id ? { ...item, ...patch } : item)),
    }));
  }, [aplicar]);

  const removerItem = useCallback(<K extends CampoLista>(campo: K, id: string) => {
    aplicar((atual) => ({
      ...atual,
      [campo]: (atual[campo] as ItemBase[]).filter((item) => item.id !== id),
    }));
  }, [aplicar]);

  const moverItem = useCallback(<K extends CampoLista>(campo: K, id: string, direcao: -1 | 1) => {
    aplicar((atual) => {
      const lista = [...(atual[campo] as ItemBase[])];
      const indice = lista.findIndex((item) => item.id === id);
      const alvo = indice + direcao;
      if (indice < 0 || alvo < 0 || alvo >= lista.length) return atual;
      [lista[indice], lista[alvo]] = [lista[alvo], lista[indice]];
      return { ...atual, [campo]: lista };
    });
  }, [aplicar]);

  const substituirLista = useCallback(<K extends CampoLista>(campo: K, itens: ManualData[K]) => {
    aplicar((atual) => ({ ...atual, [campo]: itens as ItemBase[] }));
  }, [aplicar]);

  /* -------- persistência -------- */

  /** Grava onde for o destino. Devolve false quando não conseguiu. */
  const gravar = useCallback(async (documento: ManualData): Promise<boolean> => {
    if (!manualId) {
      const ok = salvarRascunho(chave, documento);
      setEstado(ok ? 'salvo' : 'erro');
      if (ok) { setSalvoEm(new Date()); setErro(null); }
      else setErro('O rascunho não coube no armazenamento deste navegador.');
      return ok;
    }
    try {
      const registro = await salvarManual(manualId, { dados: documento, metricas: metricas() });
      setManual(registro);
      setEstado('salvo');
      setSalvoEm(new Date());
      setErro(null);
      return true;
    } catch (falha) {
      setEstado('erro');
      setErro(falha instanceof Error ? falha.message : 'Falha ao salvar no servidor.');
      // Rede caiu: o rascunho local vira a rede de segurança do trabalho aberto.
      salvarRascunho(chave, documento);
      return false;
    }
  }, [chave, manualId, metricas]);

  const salvar = useCallback(() => gravar(dados), [dados, gravar]);

  useEffect(() => {
    if (!carregado.current || estado !== 'pendente') return;
    const id = window.setTimeout(() => { void gravar(dados); }, ATRASO_SALVAMENTO);
    return () => window.clearTimeout(id);
  }, [dados, estado, gravar]);

  /** A prévia informa quantas folhas o documento tem; vai junto na gravação. */
  const registrarPaginas = useCallback((total: number) => {
    paginas.current = total;
  }, []);

  /* -------- ciclo editorial -------- */

  const definirStatus = useCallback(async (status: StatusManual) => {
    if (!manualId) return false;
    try {
      // Grava o texto antes: mudar de status com o corpo desatualizado no
      // servidor congelaria a versão errada.
      await gravar(dados);
      const registro = await salvarManual(manualId, { status, metricas: metricas() });
      setManual(registro);
      setMarcaVersoes((valor) => valor + 1);
      return true;
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível alterar o status.');
      return false;
    }
  }, [dados, gravar, manualId, metricas]);

  const registrarVersao = useCallback(async (opcoes: {
    resumo?: string;
    bumpRevisao?: boolean;
    motivo?: MotivoVersao;
  } = {}) => {
    if (!manualId) return false;
    try {
      await congelarVersao(manualId, { ...opcoes, dados });
      const { manual: registro, dados: documento } = await buscarManual(manualId);
      setManual(registro);
      if (opcoes.bumpRevisao) {
        // A revisão nova entra no documento pelo servidor; recarrega para a
        // capa e o cabeçalho refletirem o "Rev. n" gravado.
        carregado.current = false;
        setDados(documento);
        window.setTimeout(() => { carregado.current = true; }, 0);
      }
      setEstado('salvo');
      setSalvoEm(new Date());
      setMarcaVersoes((valor) => valor + 1);
      return true;
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível registrar a versão.');
      return false;
    }
  }, [dados, manualId]);

  const voltarParaVersao = useCallback(async (numero: number) => {
    if (!manualId) return false;
    try {
      await restaurarVersao(manualId, numero);
      const { manual: registro, dados: documento } = await buscarManual(manualId);
      carregado.current = false;
      passado.current = [];
      futuro.current = [];
      setManual(registro);
      setDados(documento);
      setEstado('salvo');
      setSalvoEm(new Date());
      setMarcaHistorico((valor) => valor + 1);
      setMarcaVersoes((valor) => valor + 1);
      window.setTimeout(() => { carregado.current = true; }, 0);
      return true;
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível restaurar a versão.');
      return false;
    }
  }, [manualId]);

  /* -------- arquivo -------- */
  const exportar = useCallback(() => {
    const nome = normalizarNomeArquivo(dados.nomeMaquina || dados.identificacao);
    baixarArquivo(`manual-${nome}.json`, serializarManual(dados));
  }, [dados]);

  const importar = useCallback(async (arquivo: File) => {
    const conteudo = await arquivo.text();
    const importado = desserializarManual(conteudo);
    aplicar(() => importado);
    return importado;
  }, [aplicar]);

  const limpar = useCallback(() => {
    aplicar(() => dadosIniciais());
    if (!manualId) {
      removerRascunho(chave);
      setEstado('ocioso');
      setSalvoEm(null);
    }
  }, [aplicar, chave, manualId]);

  return {
    dados,
    manual,
    manualId: manualId ?? null,
    destino,
    avaliacao,
    estado,
    salvoEm,
    carregando,
    erro,
    marcaVersoes,
    podeDesfazer: passado.current.length > 0,
    podeRefazer: futuro.current.length > 0,
    marcaHistorico,
    definirCampo,
    definirOpcao,
    adicionarItem,
    adicionarItens,
    atualizarItem,
    removerItem,
    moverItem,
    substituirLista,
    registrarPaginas,
    desfazer,
    refazer,
    salvar,
    definirStatus,
    registrarVersao,
    voltarParaVersao,
    exportar,
    importar,
    limpar,
  };
}

export type ManualEditor = ReturnType<typeof useManualEditor>;
